import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2, Tag, Calendar, CheckCircle2, Clock, X } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

interface ReleaseChange {
  type: 'feature' | 'fix' | 'improvement' | 'breaking';
  description: string;
}

interface Release {
  id: string;
  version: string;
  release_date: string;
  title: string;
  description: string | null;
  changes: ReleaseChange[];
  is_published: boolean;
  created_at: string;
  created_by: string | null;
}

export default function Releases() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { role } = useAuth();
  const queryClient = useQueryClient();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRelease, setEditingRelease] = useState<Release | null>(null);
  const [formData, setFormData] = useState({
    version: '',
    title: '',
    description: '',
    is_published: false,
    changes: [] as ReleaseChange[]
  });
  const [newChange, setNewChange] = useState<ReleaseChange>({ type: 'feature', description: '' });

  const { data: releases, isLoading } = useQuery({
    queryKey: ['releases'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('releases')
        .select('*')
        .order('release_date', { ascending: false });
      
      if (error) throw error;
      return (data || []).map(r => ({
        ...r,
        changes: (r.changes as unknown as ReleaseChange[]) || []
      }));
    }
  });

  const createMutation = useMutation({
    mutationFn: async (formData: { version: string; title: string; description: string; is_published: boolean; changes: ReleaseChange[] }) => {
      const { error } = await supabase
        .from('releases')
        .insert([{
          version: formData.version,
          title: formData.title,
          description: formData.description || null,
          is_published: formData.is_published,
          changes: JSON.parse(JSON.stringify(formData.changes))
        }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      toast({ title: 'Release creata', description: 'La nuova release è stata creata con successo.' });
      resetForm();
    },
    onError: () => {
      toast({ title: 'Errore', description: 'Impossibile creare la release.', variant: 'destructive' });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, formData }: { id: string; formData: { version: string; title: string; description: string; is_published: boolean; changes: ReleaseChange[] } }) => {
      const { error } = await supabase
        .from('releases')
        .update({
          version: formData.version,
          title: formData.title,
          description: formData.description || null,
          is_published: formData.is_published,
          changes: JSON.parse(JSON.stringify(formData.changes))
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      toast({ title: 'Release aggiornata', description: 'La release è stata aggiornata con successo.' });
      resetForm();
    },
    onError: () => {
      toast({ title: 'Errore', description: 'Impossibile aggiornare la release.', variant: 'destructive' });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('releases')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      toast({ title: 'Release eliminata', description: 'La release è stata eliminata.' });
    },
    onError: () => {
      toast({ title: 'Errore', description: 'Impossibile eliminare la release.', variant: 'destructive' });
    }
  });

  const resetForm = () => {
    setFormData({ version: '', title: '', description: '', is_published: false, changes: [] });
    setEditingRelease(null);
    setIsDialogOpen(false);
    setNewChange({ type: 'feature', description: '' });
  };

  const handleEdit = (release: Release) => {
    setEditingRelease(release);
    setFormData({
      version: release.version,
      title: release.title,
      description: release.description || '',
      is_published: release.is_published,
      changes: release.changes || []
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.version || !formData.title) {
      toast({ title: 'Errore', description: 'Versione e titolo sono obbligatori.', variant: 'destructive' });
      return;
    }
    
    if (editingRelease) {
      updateMutation.mutate({ id: editingRelease.id, formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const addChange = () => {
    if (newChange.description.trim()) {
      setFormData(prev => ({
        ...prev,
        changes: [...prev.changes, { ...newChange }]
      }));
      setNewChange({ type: 'feature', description: '' });
    }
  };

  const removeChange = (index: number) => {
    setFormData(prev => ({
      ...prev,
      changes: prev.changes.filter((_, i) => i !== index)
    }));
  };

  const getChangeTypeBadge = (type: string) => {
    const styles: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
      feature: { variant: 'default', label: 'Nuova funzionalità' },
      fix: { variant: 'secondary', label: 'Correzione' },
      improvement: { variant: 'outline', label: 'Miglioramento' },
      breaking: { variant: 'destructive', label: 'Breaking change' }
    };
    const style = styles[type] || styles.feature;
    return <Badge variant={style.variant}>{style.label}</Badge>;
  };

  if (role !== 'admin') {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Accesso non autorizzato</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Release Notes</h1>
            <p className="text-muted-foreground">Gestisci le versioni e il changelog del sistema</p>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button onClick={() => setIsDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Nuova Release
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingRelease ? 'Modifica Release' : 'Nuova Release'}</DialogTitle>
                <DialogDescription>
                  {editingRelease ? 'Modifica i dettagli della release.' : 'Crea una nuova release con il changelog.'}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="version">Versione *</Label>
                    <Input
                      id="version"
                      placeholder="es. 1.0.0"
                      value={formData.version}
                      onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="title">Titolo *</Label>
                    <Input
                      id="title"
                      placeholder="es. Rilascio iniziale"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Descrizione</Label>
                  <Textarea
                    id="description"
                    placeholder="Descrizione generale della release..."
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_published"
                    checked={formData.is_published}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))}
                  />
                  <Label htmlFor="is_published">Pubblica release</Label>
                </div>
                
                <div className="space-y-3">
                  <Label>Modifiche</Label>
                  
                  <div className="flex gap-2">
                    <select
                      className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={newChange.type}
                      onChange={(e) => setNewChange(prev => ({ ...prev, type: e.target.value as ReleaseChange['type'] }))}
                    >
                      <option value="feature">Nuova funzionalità</option>
                      <option value="fix">Correzione</option>
                      <option value="improvement">Miglioramento</option>
                      <option value="breaking">Breaking change</option>
                    </select>
                    <Input
                      placeholder="Descrizione della modifica..."
                      value={newChange.description}
                      onChange={(e) => setNewChange(prev => ({ ...prev, description: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addChange())}
                      className="flex-1"
                    />
                    <Button type="button" variant="outline" onClick={addChange}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {formData.changes.length > 0 && (
                    <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-3">
                      {formData.changes.map((change, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm">
                          {getChangeTypeBadge(change.type)}
                          <span className="flex-1">{change.description}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeChange(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={resetForm}>Annulla</Button>
                <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
                  {editingRelease ? 'Salva modifiche' : 'Crea Release'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : releases?.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Tag className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Nessuna release</h3>
              <p className="text-muted-foreground text-center mb-4">
                Non ci sono ancora release. Crea la prima release per iniziare a tracciare le versioni.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {releases?.map((release) => (
              <Card key={release.id} className={!release.is_published ? 'border-dashed opacity-70' : ''}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-base font-mono">
                          v{release.version}
                        </Badge>
                        <CardTitle className="text-xl">{release.title}</CardTitle>
                        {release.is_published ? (
                          <Badge variant="default" className="gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Pubblicata
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="gap-1">
                            <Clock className="h-3 w-3" />
                            Bozza
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {format(new Date(release.release_date), 'dd MMMM yyyy, HH:mm', { locale: it })}
                      </CardDescription>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(release)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteMutation.mutate(release.id)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {release.description && (
                    <p className="text-muted-foreground">{release.description}</p>
                  )}
                  
                  {release.changes && release.changes.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Changelog:</h4>
                      <ul className="space-y-2">
                        {release.changes.map((change, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm">
                            {getChangeTypeBadge(change.type)}
                            <span>{change.description}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
